<template>
  <loading-placeholder v-if="isLoading && !hasCachedMemberData" :lines="1" class="project-home-header-options-loading" />
  <div v-else class="project-home-header-options marker--project-home-header-options">
    <div class="project-home-header-options__options">
      <!-- Scheduled Tasks -->
      <el-tooltip v-if="!$permissions.isGuest && !$permissions.isAppSumoAccount" placement="top">
        <template #content>
          <div>Go to Scheduled Tasks</div>
        </template>
        <router-link :to="RouteAssistant.getProjectScheduleRoute(accountId, projectId)" title="Scheduled Tasks">
          <i class="main-icon ddi ddi-clock" />
        </router-link>
      </el-tooltip>
      <!-- Export -->
      <item-export-picker
        v-if="$permissions.isAdminOrOwner"
        :entity-type="EEntities.PROJECTS"
        :entity-id="projectId"
        :total-item-count="totalItemCount"
        class="main-icon"
        @import-clicked="$emit('import-clicked')"
      />
      <!-- Notifications -->
      <email-notifications-picker 
        :entity-type="EEntities.PROJECTS"
        :entity-id="projectId"
        class="main-icon" 
      />
      <!-- Calendar -->
      <el-tooltip placement="top">
        <template #content>
          <div>Go to Calendar</div>
        </template>
        <router-link :to="RouteAssistant.getCalendarRoute(accountId, projectId)" title="Calendar">
          <i class="main-icon ddi ddi-calendar" />
        </router-link>
      </el-tooltip>
      <!-- Reports -->
      <el-tooltip placement="top">
        <template #content>
          <div>Go to Reports</div>
        </template>
        <a :href="RouteAssistant.getReportsUrl(accountId, projectId)" title="Reports">
          <i class="main-icon ddi ddi-reports" />
        </a>
      </el-tooltip>
      <!-- Settings -->
      <more-button-dropdown v-if="$permissions.isAdminOrOwner" title="Settings">
        <template #options-list>
          <template v-for="menu in menuList" :key="menu.label">
            <h5>{{menu.label}}</h5>
            <ul>
              <li
                v-for="option in menu.options"
                :key="option.value"
                @click="sendToSettingsLink(option.value)"
              >
                <i :class="'ddi-'+option.icon" class="ddi" />
                <span>{{option.label}}</span>
              </li>
            </ul>
          </template>
        </template>
      </more-button-dropdown>
      <span class="vertical-divider" />
    </div>
    <!-- cachedMemberData.members will always appear here because: 
      (1) It immediately shows (because it was already cached)
      (2) It is updated after members prop is updated (which updates cache via members watch)
          Thus, no need to show loading...allow the data to be 'stale' momentarily until parent
          provides the prop.
      (3) Otherwise, isLoading invoked above until members prop is hydrated
    -->
    <avatar-gallery
      :people="cachedMemberData ? cachedMemberData.members : members"
      :max-num-avatars="5"
      stack-tightly
    />
    <quick-add-member-picker
      v-if="$permissions.isAdminOrOwner"
      :entity-type="EEntities.PROJECTS"
      :entity-id="projectId"
      class="project-home-header-options__add-people-button"
      @members-added="$emit('members-added')"
    />
    <div
      v-if="!$permissions.isGuest && !$permissions.isAppSumoAccount"
      class="project-home-header-options__new-scheduled-task-button"
      @click="$emit('new-scheduled-task-clicked')"
    >
      <dd-button
        :color="ColorAssistant.PeopleColor"
        size="medium"
        type="flat"
        new-icon
      ><i class="ddi ddi-clock" />&nbsp;<span>Schedule</span></dd-button>
    </div>
    <div class="project-home-header-options__new-task-button" @click="$emit('new-task-clicked')">
      <new-task-button
        :project-id="projectId"
        :project-name="projectName"
        disable-click
        class="marker--project-home-header-options--new-task-button"
        size="medium" />
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import * as routerNames from '@/services/router/router.names'
import {
  ContentPlaceholders,
  ContentPlaceholdersText,
} from '@/components/global/content-placeholders'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IPersonBase } from '@/api/types/response.types'
import { EEntities } from '@/types'
import {
  IMemberDataFromEntityLanding,
  INavMenuGroup,
} from '@/types/data-objects/global'
import { useAppStore } from '@/modules/app/app.store'

/* API */

export default defineComponent({
  name: 'ProjectHomeHeaderOptions',

  components: {
    ContentPlaceholders,
    ContentPlaceholdersText,
  },

  props: {
    members: {
      type: Array as () => IPersonBase[],
      required: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    isKanban: {
      type: Boolean,
      default: false,
    },

    accountId: {
      type: Number,
      required: true,
    },

    projectId: {
      type: Number,
      required: true,
    },

    projectName: {
      type: String,
      default: '',
    },

    totalItemCount: {
      type: Number,
      default: null,
    },
  },

  emits: [
    'import-clicked',
    'members-added',
    'new-scheduled-task-clicked',
    'new-task-clicked',
  ],

  data() {
    return {
      EEntities,
      RouteAssistant,
      ColorAssistant,
    }
  },

  computed: {
    cachedMemberData(): IMemberDataFromEntityLanding {
      return useAppStore().lastVisitedEntityLandingMemberData
    },

    hasCachedMemberData(): boolean {
      return (
        this.cachedMemberData &&
        this.cachedMemberData.entityID === this.projectId
      )
    },

    menuList(): INavMenuGroup[] {
      const items = [
        {
          label: 'Manage',
          options: [
            {
              value: routerNames.PROJECT_SETTINGS_ASSIGN_PEOPLE,
              label: 'Add/Remove Users',
              icon: 'people',
            },
            ...(this.$permissions.isTieredPricingPlan
              ? [
                  {
                    value: routerNames.PROJECT_SETTINGS_ASSIGN_GUESTS,
                    label: 'Add/Remove Guests',
                    icon: 'people',
                  },
                ]
              : []),
            {
              value: routerNames.PROJECT_SETTINGS_CHECKLIST_TEMPLATES,
              label: 'Checklist Templates',
              icon: 'everything',
            },
            {
              value: routerNames.PROJECT_SETTINGS_CUSTOM_FIELDS,
              label: 'Custom Fields',
              icon: 'three-blocks',
            },
            {
              value: routerNames.PROJECT_SETTINGS_DESCRIPTION_TEMPLATES,
              label: 'Description Templates',
              icon: 'description',
            },
            {
              value: routerNames.PROJECT_SETTINGS_EMAIL_SETTINGS,
              label: 'Email Settings',
              icon: 'incoming-email',
            },
            {
              value: routerNames.PROJECT_SETTINGS_MANAGE_WORKFLOW,
              label: 'Manage Workflow',
              icon: 'workflows',
            },
            {
              value: routerNames.PROJECT_SETTINGS_WORKFLOW_AUTOMATIONS,
              label: 'Workflow Automations',
              icon: 'workflows',
            },
            {
              value: routerNames.PROJECT_SETTINGS_GENERAL_SETTINGS,
              label: 'General Settings',
              icon: 'cog',
            },
            {
              value: routerNames.PROJECT_SETTINGS_INTEGRATIONS,
              label: 'Integrations',
              icon: 'integrations',
            },
          ],
        },
      ]

      return items
    },
  },

  watch: {
    members: {
      deep: 1,
      handler() {
        this.cacheMemberDataFromEntityLanding()
      },
    },
  },

  methods: {
    cacheMemberDataFromEntityLanding() {
      const data = {
        entityID: this.projectId,
        members: this.members,
      } as IMemberDataFromEntityLanding

      useAppStore().lastVisitedEntityLandingMemberData = data
    },
    sendToSettingsLink(value: string) {
      this.$router.push({
        name: value,
        params: {
          accountId: this.$route.params.accountId,
          id: this.$route.params.id,
        },
      })
    },
  },
})
</script>

<style lang="scss">
.project-home-header-options {
  display: flex;
  align-items: center;

  &-loading {
    position: relative;
    // Makes it look good on mobile too (with 10% side padding on vertical stacked layout)
    width: 90%;
    height: 32px;

    .vue-content-placeholders-text__line {
      position: absolute;
      width: 100% !important;
      height: 100%;
    }
  }

  &__options {
    display: flex;
    align-items: center;

    .main-icon {
      font-size: 21px;
      padding: 0 8px;
      @include clickable-gray-color-states;
    }
  }

  .vertical-divider {
    width: 1px;
    height: 24px;
    margin: 0 12px;
    background-color: $color--gray-30;
  }

  &__new-scheduled-task-button {
    margin-left: 24px;
  }

  &__add-people-button {
    margin-left: 6px;
  }

  &__new-task-button {
    margin-left: 6px;
  }
}
</style>
